import { useId, useState, type ReactNode } from "react";

export const PLUM = "#8E6C8A";
export const BLUE = "#0081AF";
export const GREEN = "#2A9D8F";
export const CLAY = "#C84C35";
export const GOLD = "#E4B363";

export const BOOKED_HELP_TEXT =
  "Booked means the carespace had a non-cancelled appointment scheduled after the first email in this wave and before the reporting cutoff. It does not prove the email caused the booking.";
export const DCMP_ENCOUNTER_HELP_TEXT =
  "DCMP encounter means the carespace had a DCMP billing entry dated on or after the first email in this wave and before the reporting cutoff. It does not prove the email caused the encounter.";

export function currentMonthInTimezone(timeZone: string, referenceDate = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(referenceDate);
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  return `${year}-${month}`;
}

export function toNumber(value: unknown): number {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

export function fmtInt(value: unknown): string {
  return Math.round(toNumber(value)).toLocaleString();
}

export function fmtPct(value: unknown): string {
  return `${(toNumber(value) * 100).toFixed(1)}%`;
}

export function fmtSignedInt(value: unknown): string {
  const numeric = Math.round(toNumber(value));
  return `${numeric > 0 ? "+" : ""}${numeric.toLocaleString()}`;
}

export function fmtPointChange(value: unknown): string {
  const points = toNumber(value) * 100;
  return `${points > 0 ? "+" : ""}${points.toFixed(1)} pp`;
}

export function fmtMonth(value: string): string {
  return new Date(`${value}-01T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

export function fmtDate(value: string): string {
  return new Date(`${value}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function fmtDateTime(value: string): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

export function pct(numerator: unknown, denominator: unknown): number {
  const den = toNumber(denominator);
  return den > 0 ? toNumber(numerator) / den : 0;
}

export function hasVisibleGroupData(row: {
  bookedCount: unknown;
  carespacesSent: unknown;
  deliveredCount: unknown;
  deliveryIssueCount: unknown;
  emailsSent: unknown;
  encounteredCount: unknown;
  repliedCount: unknown;
}): boolean {
  return [
    row.carespacesSent,
    row.emailsSent,
    row.deliveredCount,
    row.deliveryIssueCount,
    row.repliedCount,
    row.bookedCount,
    row.encounteredCount,
  ].some((value) => toNumber(value) > 0);
}

export function InfoHint({ text }: { text: string }) {
  const tooltipId = useId();
  const [open, setOpen] = useState(false);

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        aria-label={text}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-border bg-background/80 text-[0.65rem] font-bold text-muted-foreground"
      >
        ?
      </button>
      {open ? (
        <span
          id={tooltipId}
          role="tooltip"
          className="absolute top-[calc(100%+8px)] left-0 z-20 w-[260px] rounded-md bg-foreground px-3 py-2 text-xs font-normal normal-case tracking-normal text-background shadow-md"
        >
          {text}
        </span>
      ) : null}
    </span>
  );
}

export function MetricLabel({ helpText, label }: { helpText?: string | undefined; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{label}</span>
      {helpText ? <InfoHint text={helpText} /> : null}
    </span>
  );
}

export function MetricCard({
  color,
  face,
  helpText,
  label,
  sub,
  value,
}: {
  color: string;
  face: string;
  helpText?: string | undefined;
  label: string;
  sub?: string | undefined;
  value: string;
}) {
  return (
    <article className="flex h-full min-w-0 flex-col overflow-visible rounded-lg border border-border/70 bg-background">
      <div className="h-1.5 shrink-0" style={{ background: color }} />
      <div className="flex flex-1 flex-col rounded-b-lg px-4 py-3" style={{ background: face }}>
        <div className="mb-1.5 text-[0.7rem] font-bold tracking-wide text-muted-foreground uppercase">
          <MetricLabel label={label} helpText={helpText} />
        </div>
        <div className="text-3xl font-bold leading-none text-foreground">{value}</div>
        <div className="mt-1 min-h-[2.5rem] text-xs leading-snug text-muted-foreground line-clamp-3">
          {sub ?? "\u00a0"}
        </div>
      </div>
    </article>
  );
}

export function MetricCardGrid(props: { children: ReactNode; className?: string }) {
  return (
    <div
      className={
        props.className ??
        "grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6"
      }
    >
      {props.children}
    </div>
  );
}

export function Section({
  children,
  subtitle,
  title,
}: {
  children: ReactNode;
  subtitle?: string;
  title: string;
}) {
  return (
    <section className="rounded-lg border border-border/70 bg-background p-5 shadow-xs">
      <div className="mb-3.5">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {subtitle ? (
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}
